import { useHubble } from '@seek/hubble';
import { useCallback } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import {
  FEATURE_CART_AND_PAY_AND_POST_V2,
  useFeatureToggleData,
} from 'src/app/shared/featureToggle';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { log } from 'src/app/utils/logging';
import type { AppState } from 'src/types/appState';

export const useHubbleTrackEventHandler = () => {
  const { draftId, flowType, pageId, productSetId } = useSelector(
    (state: AppState) => ({
      draftId: state.session.draftId,
      flowType: state.tealium.cajaFlowType,
      pageId: state.session.pageId,
      productSetId: state.session.productSet?.id,
    }),
    shallowEqual,
  );

  const hubble = useHubble();
  const isCartEnabled = useFeatureToggleData(FEATURE_CART_AND_PAY_AND_POST_V2);
  const { cartState: { productSetId: productSetIdFromCart } = {} } =
    useShoppingCart();
  const productSetIdForHubble = isCartEnabled
    ? productSetIdFromCart
    : productSetId;

  const sendHubbleTrackEvent = useCallback(
    (eventName: any, eventData: any) => {
      if (draftId && flowType && pageId && productSetIdForHubble) {
        hubble.trackEvent(eventName, {
          ...eventData,
          draftId,
          postingFlowType: flowType,
          postingFlowPageName: pageId,
          productSetId: productSetIdForHubble,
          opportunityId: draftId,
        });
      } else {
        log.warn(
          'useHubbleTrackEventHandler | missing required data for tracking',
          {
            draftId,
            flowType,
            pageId,
            productSetIdForHubble,
          },
        );
      }
    },
    [hubble, draftId, flowType, pageId, productSetIdForHubble],
  );

  return { sendHubbleTrackEvent };
};
